import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  Grid2 as Grid,
  TablePagination,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Psychology as PromptIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { ChatPromptDto } from "@/dto/ChatPromptDto";
import { Yn } from "@/constants/code";

/**
 * 프롬프트 목록 컴포넌트 Props
 */
export interface ListProps {
  /**
   * 프롬프트 목록
   */
  list: ChatPromptDto[];

  /**
   * 로딩 상태
   */
  isLoading?: boolean;

  /**
   * 페이지 번호
   */
  page?: number;

  /**
   * 전체 개수
   */
  count?: number;

  /**
   * 페이지 변경 핸들러
   */
  onPageChange?: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;

  /**
   * 페이지당 항목 수 변경 핸들러
   */
  onRowsPerPageChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;

  /**
   * 페이지당 항목 수
   */
  rowsPerPage?: number;

  /**
   * 상세 보기 핸들러
   */
  onView?: (prompt: ChatPromptDto) => void;

  /**
   * 새로고침 핸들러
   */
  onRefresh?: () => void;
}

/**
 * 프롬프트 목록 컴포넌트
 * @description QNA 채팅 프롬프트 목록을 표시합니다.
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const List = (props: ListProps) => {
  const {
    list = [],
    isLoading = false,
    page = 1,
    count = 0,
    rowsPerPage = 20,
    onPageChange,
    onRowsPerPageChange,
    onView,
    onRefresh,
  } = props;

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (list.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center" }}>
        <PromptIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          등록된 프롬프트가 없습니다
        </Typography>
        <Typography variant="body2" color="text.secondary">
          새로운 프롬프트를 생성해보세요.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* 프롬프트 개수 정보 */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" color="text.primary">
          총 {count.toLocaleString()}개의 프롬프트
        </Typography>
        <IconButton onClick={onRefresh} size="small" color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* AI 파라미터 설명 카드 */}
      <Card sx={{ mb: 3, bgcolor: "background.default" }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PromptIcon color="primary" />
            AI 모델 파라미터 가이드
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  🌡️ Temperature (창의성 조절)
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  AI 응답의 창의성과 일관성을 조절하는 값입니다.
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="caption" display="block">
                    • <strong>0.0 ~ 0.3:</strong> 매우 정확하고 일관된 답변
                    (FAQ, 정보 안내)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>0.4 ~ 0.7:</strong> 균형잡힌 답변 (일반 상담)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>0.8 ~ 2.0:</strong> 창의적이고 다양한 답변
                    (브레인스토밍)
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  📝 Max Tokens (응답 길이 제한)
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  AI가 생성할 수 있는 최대 응답 길이를 제한합니다.
                </Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="caption" display="block">
                    • <strong>100 ~ 1,000:</strong> 짧은 답변 (간단한 안내)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>1,000 ~ 4,000:</strong> 보통 길이 답변 (상세 설명)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>4,000 ~ 8,000:</strong> 긴 답변 (복잡한 문의 처리)
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>프롬프트 키</TableCell>
              <TableCell>AI 모델</TableCell>
              <TableCell>Temperature</TableCell>
              <TableCell>Max Tokens</TableCell>
              <TableCell>우선순위</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>등록일</TableCell>
              <TableCell align="center">상세</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.map((prompt) => (
              <TableRow key={prompt.promptId} hover>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {prompt.promptKey}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        maxWidth: 200,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {prompt.promptContent}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {prompt.aiModelProvider}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {prompt.aiModelName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={prompt.aiModelTemperature}
                    size="small"
                    color={
                      prompt.aiModelTemperature <= 0.3
                        ? "primary"
                        : prompt.aiModelTemperature <= 0.7
                        ? "default"
                        : "warning"
                    }
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {prompt.aiMaxTokens.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={prompt.priorityOrder}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={prompt.useYn === Yn.Y ? "활성" : "비활성"}
                    size="small"
                    color={prompt.useYn === Yn.Y ? "success" : "default"}
                    variant={prompt.useYn === Yn.Y ? "filled" : "outlined"}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {new Date(prompt.regDttm).toLocaleDateString("ko-KR")}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="상세 보기">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onView?.(prompt)}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 페이지네이션 */}
      {count > 0 && (
        <TablePagination
          component="div"
          count={count}
          page={page - 1} // MUI는 0-based, 내부 상태는 1-based
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="페이지당 항목 수:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} / 총 ${count !== -1 ? count : `${to}개 이상`}개`
          }
        />
      )}
    </Box>
  );
};
